import React, { useEffect, useState } from 'react';
import { Input } from '../ui/Input';
import { useCalculatorStore, DEFAULT_GDS_TDS } from '../../store/calculatorStore';

interface GDSTDSCalculatorProps {
  tabId: string;
  mortgagePayment: number;
  monthlyPropertyTax: number;
}

interface RatioResult {
  ratio: number;
  status: 'good' | 'borderline' | 'over';
  max: number;
  label: string;
}

const getRatioStatus = (ratio: number, max: number, borderlineThreshold: number): RatioResult['status'] => {
  if (ratio > max) return 'over';
  if (ratio >= borderlineThreshold) return 'borderline';
  return 'good';
};

const statusConfig = {
  good: {
    bar: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 border-green-200',
    text: 'text-green-700',
    icon: '✓',
    label: 'Within limit',
  },
  borderline: {
    bar: 'bg-yellow-500',
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    text: 'text-yellow-700',
    icon: '⚠',
    label: 'Borderline',
  },
  over: {
    bar: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 border-red-200',
    text: 'text-red-700',
    icon: '✕',
    label: 'Over limit',
  },
};

interface RatioBarProps {
  ratio: number;
  max: number;
  label: string;
  description: string;
  status: RatioResult['status'];
}

const RatioBar: React.FC<RatioBarProps> = ({ ratio, max, label, description, status }) => {
  const config = statusConfig[status];
  const fillPct = Math.min((ratio / max) * 100, 100);
  const remaining = Math.max(max - ratio, 0);
  const isOver = status === 'over';

  return (
    <div className={`p-3 rounded-md border ${config.badge}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm">{label}</span>
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded border ${config.badge}`}>
            {config.icon} {config.label}
          </span>
        </div>
        <span className="font-bold text-base">{ratio.toFixed(1)}%</span>
      </div>
      <p className="text-xs mb-2 opacity-75">{description}</p>
      <div className="w-full bg-white bg-opacity-60 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-2.5 rounded-full transition-all duration-300 ${config.bar}`}
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <div className="flex justify-between text-xs mt-1 opacity-80">
        <span>0%</span>
        <span>
          {isOver
            ? `${(ratio - max).toFixed(1)}% over limit`
            : `${remaining.toFixed(1)}% remaining`}
        </span>
        <span>Max {max}%</span>
      </div>
    </div>
  );
};

export const GDSTDSCalculator: React.FC<GDSTDSCalculatorProps> = ({
  tabId,
  mortgagePayment,
  monthlyPropertyTax,
}) => {
  const { gdsTds, expandGdsTdsPanel } = useCalculatorStore((s) => {
    const tab = s.calculatorTabs.find((t) => t.id === tabId);
    return {
      gdsTds: tab?.data.gdsTds ?? DEFAULT_GDS_TDS,
      expandGdsTdsPanel: tab?.data.expandGdsTdsPanel ?? false,
    };
  });
  const updateCalculatorTabData = useCalculatorStore((s) => s.updateCalculatorTabData);
  const { monthlyIncome, heatingCosts, condoFees, otherDebts } = gdsTds;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (expandGdsTdsPanel) {
      setIsExpanded(true);
    }
  }, [expandGdsTdsPanel]);

  const updateGdsTds = (field: keyof typeof gdsTds, value: number) => {
    updateCalculatorTabData(tabId, { gdsTds: { ...gdsTds, [field]: value } });
  };

  // GDS = (mortgage + property tax + heating + 50% condo fees) / gross income × 100
  const gdsNumerator = mortgagePayment + monthlyPropertyTax + heatingCosts + condoFees * 0.5;
  const gdsRatio = monthlyIncome > 0 ? (gdsNumerator / monthlyIncome) * 100 : 0;

  // TDS = (GDS costs + all other debt payments) / gross income × 100
  const tdsNumerator = gdsNumerator + otherDebts;
  const tdsRatio = monthlyIncome > 0 ? (tdsNumerator / monthlyIncome) * 100 : 0;

  const GDS_MAX = 39;
  const TDS_MAX = 44;
  const GDS_BORDERLINE = 35;
  const TDS_BORDERLINE = 40;

  const gdsStatus = monthlyIncome > 0 ? getRatioStatus(gdsRatio, GDS_MAX, GDS_BORDERLINE) : 'good';
  const tdsStatus = monthlyIncome > 0 ? getRatioStatus(tdsRatio, TDS_MAX, TDS_BORDERLINE) : 'good';

  const bothQualify = monthlyIncome > 0 && gdsStatus !== 'over' && tdsStatus !== 'over';
  const hasInput = monthlyIncome > 0;

  return (
    <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-md">
      <button
        type="button"
        className="flex items-center justify-between w-full text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div>
          <h4 className="font-semibold text-teal-900">GDS / TDS Qualification Calculator</h4>
          <p className="text-xs text-teal-700 mt-0.5">Canadian Mortgage Stress Test Ratios</p>
        </div>
        <span className="text-teal-600 text-sm select-none">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <>
          {/* Educational callout */}
          <div className="mt-3 p-3 bg-teal-100 rounded-md">
            <p className="text-xs text-teal-900 font-medium mb-1">What are GDS and TDS ratios?</p>
            <ul className="text-xs text-teal-800 space-y-1 ml-3">
              <li>
                • <strong>GDS</strong> (Gross Debt Service): Housing costs ÷ gross income. Maximum <strong>39%</strong>.
              </li>
              <li>
                • <strong>TDS</strong> (Total Debt Service): All debt obligations ÷ gross income. Maximum <strong>44%</strong>.
              </li>
              <li>• Canadian lenders use these to assess mortgage affordability as part of the stress test.</li>
            </ul>
          </div>

          {/* Input fields */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div>
              <div className="max-w-[280px]">
                <Input
                  label="Monthly Gross Household Income"
                  type="number"
                  value={monthlyIncome || ''}
                  onChange={(e) => updateGdsTds('monthlyIncome', Number(e.target.value))}
                  prefix="$"
                  formatWithCommas={true}
                />
              </div>
              <div className="max-w-[280px]">
                <Input
                  label="Monthly Heating Costs"
                  type="number"
                  value={heatingCosts || ''}
                  onChange={(e) => updateGdsTds('heatingCosts', Number(e.target.value))}
                  prefix="$"
                  formatWithCommas={true}
                />
                <p className="text-xs text-teal-700 mt-1 mb-4">Typical range: $100 - $300/month</p>
              </div>
            </div>
            <div>
              <div className="max-w-[280px]">
                <Input
                  label="Monthly Condo Fees (if applicable)"
                  type="number"
                  value={condoFees || ''}
                  onChange={(e) => updateGdsTds('condoFees', Number(e.target.value))}
                  prefix="$"
                  formatWithCommas={true}
                />
                <p className="text-xs text-teal-700 mt-1 mb-4">Only 50% of condo fees is included in GDS</p>
              </div>
              <div className="max-w-[280px]">
                <Input
                  label="Other Monthly Debt Payments"
                  type="number"
                  value={otherDebts || ''}
                  onChange={(e) => updateGdsTds('otherDebts', Number(e.target.value))}
                  prefix="$"
                  formatWithCommas={true}
                />
                <p className="text-xs text-teal-700 mt-1 mb-4">Car loans, credit cards, student loans, etc.</p>
              </div>
            </div>
          </div>

          {/* Auto-populated values summary */}
          <div className="mb-4 p-3 bg-white border border-teal-200 rounded-md text-xs text-teal-800">
            <p className="font-medium mb-1 text-teal-900">Values used from your mortgage:</p>
            <div className="flex flex-wrap gap-x-6 gap-y-0.5">
              <span>Mortgage payment: <strong>${mortgagePayment.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</strong></span>
              <span>Property tax: <strong>${monthlyPropertyTax.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</strong></span>
            </div>
          </div>

          {/* Results */}
          {!hasInput ? (
            <div className="p-3 bg-white border border-teal-200 rounded-md text-center text-sm text-teal-600">
              Enter your monthly gross household income above to calculate your GDS and TDS ratios.
            </div>
          ) : (
            <div className="space-y-3">
              <RatioBar
                ratio={gdsRatio}
                max={GDS_MAX}
                label="GDS Ratio"
                description={`(Mortgage $${mortgagePayment.toFixed(0)} + Tax $${monthlyPropertyTax.toFixed(0)} + Heating $${heatingCosts.toFixed(0)} + 50% condo $${(condoFees * 0.5).toFixed(0)}) ÷ Income $${monthlyIncome.toFixed(0)}`}
                status={gdsStatus}
              />
              <RatioBar
                ratio={tdsRatio}
                max={TDS_MAX}
                label="TDS Ratio"
                description={`GDS costs + Other debts $${otherDebts.toFixed(0)} ÷ Income $${monthlyIncome.toFixed(0)}`}
                status={tdsStatus}
              />

              {/* Overall qualification result */}
              <div
                className={`p-3 rounded-md border text-sm font-medium ${
                  bothQualify
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {bothQualify ? (
                  <>✓ You likely qualify based on GDS/TDS ratios.</>
                ) : (
                  <>
                    ✕ One or more ratios exceed the lender limit.
                    {gdsStatus === 'over' && (
                      <span className="block mt-0.5 text-xs font-normal">
                        GDS is {(gdsRatio - GDS_MAX).toFixed(1)}% over the {GDS_MAX}% limit.
                      </span>
                    )}
                    {tdsStatus === 'over' && (
                      <span className="block mt-0.5 text-xs font-normal">
                        TDS is {(tdsRatio - TDS_MAX).toFixed(1)}% over the {TDS_MAX}% limit.
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-teal-200">
            <p className="text-xs text-teal-700">
              <strong>Disclaimer:</strong> These ratios are estimates for informational purposes. Individual lenders may use different thresholds, and the mortgage stress test requires qualifying at the higher of your contract rate + 2% or the Bank of Canada's qualifying rate. Consult a mortgage professional for personalized advice.
            </p>
          </div>
        </>
      )}
    </div>
  );
};
